import {
  PartnerDataFormKeys,
  PartnerDataFormSettings,
} from 'legacy/lut/components/widgets/public/onboarding/onbPartnerForm/configs'
import { IPartnerDataFormData } from 'legacy/lut/components/widgets/public/onboarding/onbPartnerForm/models'
import { ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS } from 'legacy/lut/components/widgets/public/onboarding/onbPartnerForm/translations'
import { fetchPartnerRequestById } from 'legacy/lut/services/partner/fetchPartnerRequestById'
import { memo, useCallback, useMemo, useState } from 'react'

import { cancelPartnerInactivationRequest } from 'clickNgo/entities/partner/api'
import { PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { formatErrorMessages, getJSendErrorMessages } from 'clickNgo/helpers/api'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import styles from './PartnerInactivatePanel.module.scss'

export const PartnerInactivePanel = memo(() => {
  const [isRejectInactivationConfirmDialogVisible, setIsRejectInactivationConfirmDialogVisible] =
    useState(false)
  const [cancellationErrorMessages, setCancellationErrorMessages] = useState<string[]>([])

  const [actualData, updateActualData] = useGetFormDataField<IPartnerDataFormData>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.ActualData
  )

  const [, setIsLoading] = useGetFormDataField<boolean>(
    PartnerDataFormKeys.Setting,
    PartnerDataFormSettings.IsLoading
  )

  const handleOnInactivationReject = useCallback(() => {
    setIsRejectInactivationConfirmDialogVisible(true)
  }, [])

  const handleOnInactivationRejectConfirm = useCallback(async () => {
    setIsRejectInactivationConfirmDialogVisible(false)
    setCancellationErrorMessages([])
    setIsLoading(true)

    try {
      const { partnerId, locationId, onboardingRequestId, source } = actualData.partner
      const result = await cancelPartnerInactivationRequest({ partnerId, locationId })
      const errorMessages = getJSendErrorMessages(result)

      if (errorMessages.length) {
        setCancellationErrorMessages(errorMessages)

        return
      }

      const partner = await fetchPartnerRequestById(partnerId, { isForce: true }).catch(() => null)

      updateActualData({
        ...actualData,
        partner: partner
          ? { ...partner, onboardingRequestId, source }
          : {
              ...actualData.partner,
              status: result.data.partnerStatus,
              workflowStatus: PartnerWorkflowStatus.NONE,
              entryId406: 0,
            },
      })
    } finally {
      setIsLoading(false)
    }
  }, [actualData, setIsLoading, updateActualData])

  const handleOnInactivationRejectReject = useCallback(() => {
    setIsRejectInactivationConfirmDialogVisible(false)
  }, [])

  const hasQueuedInactivation =
    Boolean(actualData.partner.entryId406) &&
    actualData.partner.workflowStatus === PartnerWorkflowStatus.NEW

  const content = useMemo(() => {
    return (
      <div className={styles.inactivatePanel}>
        <p>
          <Translation text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.inactivePartnerPanel.content} />
        </p>
        {hasQueuedInactivation && (
          <Button color="warning" onClick={handleOnInactivationReject}>
            <Translation
              text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.inactivePartnerPanel.button}
            />
          </Button>
        )}
      </div>
    )
  }, [handleOnInactivationReject, hasQueuedInactivation])

  const formattedCancellationErrorMessages = useMemo(
    () => formatErrorMessages(cancellationErrorMessages),
    [cancellationErrorMessages]
  )

  return (
    <>
      <InfoBlock text={content} type={InfoBlockType.Warning} />
      {cancellationErrorMessages.length > 0 && (
        <InfoBlock
          text={formattedCancellationErrorMessages}
          type={InfoBlockType.Danger}
          hasCloseButton
        />
      )}
      <ConfirmDialog
        title={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.rejectInactivationPartner.title}
        confirmBtnText={
          ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.rejectInactivationPartner.confirmBtn
        }
        rejectBtnText={
          ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.rejectInactivationPartner.rejectBtn
        }
        content={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.rejectInactivationPartner.content}
        isVisible={isRejectInactivationConfirmDialogVisible}
        onConfirm={handleOnInactivationRejectConfirm}
        onReject={handleOnInactivationRejectReject}
      />
    </>
  )
})
